import React, { useEffect, useState } from 'react';
import { EChartsOption } from 'echarts';
import BaseChart from './BaseChart';
import { CHART_NO_DATA_FOR_ARTIST } from './chartMessages';
import { artistAnalyticsService } from '../../services/api/artist-analytics.service';
import type { AudienceReachPyramidData } from '../../services/api/artist-analytics.service';

interface AudienceReachPyramidChartProps {
  artistId?: string;
  title?: string;
  className?: string;
  refreshKey?: number;
}

const AudienceReachPyramidChart: React.FC<AudienceReachPyramidChartProps> = ({
  artistId,
  title = 'Audience Reach Pyramid',
  className = '',
  refreshKey = 0
}) => {
  const [data, setData] = useState<AudienceReachPyramidData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [artistName, setArtistName] = useState<string>('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await artistAnalyticsService.getAudienceReachPyramid(artistId);
        setData(response.data);
        setArtistName(response.artistName);
      } catch (err) {
        setError('Failed to load audience reach data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [artistId, refreshKey]);

  const getChartOption = (): EChartsOption => {
    if (!data || data.length === 0) return {};

    // Sort data by value descending for proper pyramid structure
    const sortedData = [...data].sort((a, b) => b.value - a.value);

    return {
      backgroundColor: '#ffffff',
      tooltip: {
        trigger: 'item',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        borderRadius: 8,
        textStyle: {
          color: '#1f2937',
          fontSize: 12
        },
        confine: false,
        appendToBody: true,
        formatter: function(params: any) {
          const dataItem = sortedData.find(d => d.name === params.name);
          if (dataItem) {
            return `
              <div style="font-weight: 600; margin-bottom: 8px; color: #fff;">${params.name}</div>
              <div style="color: ${params.color}; margin-bottom: 4px;">● Audience: ${dataItem.formatted_audience}</div>
              <div style="color: #9ca3af; font-size: 11px;">${dataItem.metrics_count} platforms</div>
              <div style="color: #9ca3af; font-size: 11px; margin-top: 4px;">${dataItem.tier_description}</div>
              <div style="color: #9ca3af; font-size: 10px; margin-top: 4px; max-width: 250px;">${dataItem.platforms_detail}</div>
            `;
          }
          return `${params.name}: ${params.value}`;
        }
      },
      grid: {
        left: '10%',
        right: '10%',
        top: '10%',
        bottom: '10%',
        containLabel: true
      },
      series: [
        {
          name: 'Audience Reach',
          type: 'funnel',
          left: '15%',
          top: '5%',
          width: '70%',
          height: '90%',
          minSize: '5%',
          maxSize: '100%',
          sort: 'descending',
          orient: 'vertical',
          funnelAlign: 'center',
          gap: 2,
          label: {
            show: true,
            position: 'inside',
            fontSize: 12,
            fontWeight: 600,
            color: '#1f2937',
            formatter: function(params: any) {
              const dataItem = sortedData.find(d => d.name === params.name);
              return dataItem ? dataItem.formatted_audience : params.value;
            }
          },
          labelLine: {
            show: false
          },
          itemStyle: {
            borderColor: '#1C1C1C',
            borderWidth: 1,
            borderRadius: 0
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 8,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          },
          data: sortedData.map(item => ({
            name: item.name,
            value: item.value,
            itemStyle: {
              color: item.color
            }
          }))
        }
      ]
    };
  };

  if (loading) {
    return (
      <div className="w-full h-full bg-transparent p-2 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  if (error || !data || data.length === 0) {
    return (
      <div className="w-full h-full bg-transparent p-2 flex items-center justify-center">
        <p className="text-red-400 text-xs">{CHART_NO_DATA_FOR_ARTIST}</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-transparent p-2">
      <BaseChart
        option={getChartOption()}
        loading={loading}
        className="w-full h-full"
      />
    </div>
  );
};

export default AudienceReachPyramidChart;
